import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { CheckCircle, MessageCircle, ArrowLeft, Phone, MapPin, User, DollarSign, Tag, Gift, Plus, X, Save, ClipboardCopy, FileText } from 'lucide-react';
import { formatPrice, brandifyText } from '../lib/utils';
import AdminLayout from '../components/AdminLayout';
import { apiFetch } from '../lib/api';
import TagManager from '../components/TagManager';
import WhatsAppChatPanel from '../components/WhatsAppChatPanel';

interface Order {
  id: number;
  createdAt: number;
  paymentConfirmed: number;
  paymentProof?: string;
  uploads?: any[];
  name: string;
  phone: string;
  region: string;
  address: string;
  items?: string;
  totalPrice: number;
  remarks?: string;
  adminRemarks?: string;
  orderCompleted: number;
  [key: string]: any;
}

const DELIVERY_REGION_LABELS: Record<string, string> = {
  HK: '香港島',
  KLN: '九龍',
  NT: '新界',
  OS: '海外',
};

const AdminOrderDetail: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(true);
  const [confirming, setConfirming] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [refunding, setRefunding] = useState(false);
  const [activeTab, setActiveTab] = useState<'details' | 'chat'>('details');
  const [customerTags, setCustomerTags] = useState<any[]>([]);
  const [customerOrderCount, setCustomerOrderCount] = useState(0);
  const [customerTotalSpent, setCustomerTotalSpent] = useState(0);
  const [showTagModal, setShowTagModal] = useState(false);
  const [showGiftModal, setShowGiftModal] = useState(false);
  const [giftCatalog, setGiftCatalog] = useState<any[]>([]);
  const [selectedGiftKey, setSelectedGiftKey] = useState('');
  const [attachingGift, setAttachingGift] = useState(false);
  const [productImages, setProductImages] = useState<Record<number, string>>({});
  const [copied, setCopied] = useState<'ok' | 'fail' | null>(null);
  // 順豐物流面板
  const [sfData, setSfData] = useState<any>(null);
  const [sfPrint, setSfPrint] = useState('');
  const [sfMsg, setSfMsg] = useState('');
  const [sfBusy, setSfBusy] = useState(false);

  const storedUser = localStorage.getItem('admin_user');
  const currentUser = storedUser ? JSON.parse(storedUser) : null;
  const isAdmin = currentUser?.role === 'admin' || currentUser?.role === 'super_admin';

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) { navigate('/admin'); return; }
    fetchOrder();
    fetchGiftCatalog();
    fetchProductImages();
    fetchSfStatus();
  }, [id]);

  // ============================================================
  // 順豐物流面板（sf-express 模組；憑證未配置時操作會返回錯誤提示＝預期）
  // ============================================================
  const SF_STATUS_LABELS: Record<string, string> = {
    created: '已建單', picked_up: '已攬收', in_transit: '運輸中',
    delivered: '已簽收', cancelled: '已取消', exception: '異常',
  };

  const fetchSfStatus = async () => {
    try {
      const res = await apiFetch(`/api/admin/orders/${id}/sf`);
      const json = await res.json().catch(() => ({}));
      setSfData(json.data ?? json);
    } catch { /* 模組未部署時靜默 */ }
  };

  const sfAction = async (action: 'create' | 'waybill' | 'cancel') => {
    setSfBusy(true); setSfMsg('');
    try {
      const res = await apiFetch(`/api/admin/orders/${id}/sf/${action}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({}),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        setSfMsg(`❌ ${json.error || '操作失敗'}${json.detail ? `（${json.detail}）` : ''}`);
      } else if (action === 'create') {
        setSfMsg(`✓ 運單已建立：${json.data?.waybillNo || ''}`);
        await fetchSfStatus();
      } else if (action === 'waybill') {
        setSfPrint(JSON.stringify(json.data ?? {}, null, 2));
        setSfMsg('✓ 面單資料已取得（見下方輸出；列印格式待順豐文檔核準）');
      } else {
        setSfMsg('✓ 運單已取消');
        await fetchSfStatus();
      }
    } catch (e: any) {
      setSfMsg(`❌ ${e.message || '網絡錯誤'}`);
    } finally { setSfBusy(false); }
  };

  const sfRoutes = async () => {
    setSfBusy(true); setSfMsg('');
    try {
      const res = await apiFetch(`/api/admin/orders/${id}/sf/routes`);
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        setSfMsg(`❌ ${json.error || '查詢失敗'}${json.detail ? `（${json.detail}）` : ''}`);
      } else {
        setSfData((prev: any) => ({ ...(prev || {}), shipment: { ...(prev?.shipment || {}), status: json.data?.status, waybillNo: json.data?.waybillNo }, routes: json.data }));
        setSfMsg('✓ 軌跡已刷新');
      }
    } catch (e: any) {
      setSfMsg(`❌ ${e.message || '網絡錯誤'}`);
    } finally { setSfBusy(false); }
  };


  // Order items only store productId/name/qty/price — look up thumbnails from
  // the product catalog so the items list can render as horizontal rows.
  const fetchProductImages = async () => {
    try {
      const res = await apiFetch('/api/public/admin/products');
      if (!res.ok) return;
      const json = await res.json();
      const list = Array.isArray(json) ? json : (json.data ?? json.products ?? []);
      const map: Record<number, string> = {};
      list.forEach((p: any) => {
        const img = p.imageUrl || p.image_url;
        if (p.id && img) map[Number(p.id)] = String(img);
      });
      setProductImages(map);
    } catch {
      // Thumbnails are cosmetic — keep the page working without them.
    }
  };

  useEffect(() => {
    if (order?.phone) {
      fetchCustomerTags(order.phone);
    }
  }, [order]);

  const fetchOrder = async () => {
    const res = await apiFetch(`/api/public/admin/orders/${id}`);
    if (res.ok) {
      const json = await res.json();
      setOrder(json.data ?? json);
    }
    else if (res.status === 401) { localStorage.removeItem('admin_token'); navigate('/admin'); }
    setLoading(false);
  };

  const fetchGiftCatalog = async () => {
    try {
      const res = await apiFetch('/api/public/admin/gift-catalog');
      if (!res.ok) return;
      const data = await res.json();
      setGiftCatalog((Array.isArray(data) ? data : data.data || []).filter((g: any) => g.isActive));
    } catch (e) { console.error('Failed to fetch gift catalog:', e); }
  };

  const handleAttachGift = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedGiftKey || !order?.id) return;
    setAttachingGift(true);
    try {
      const res = await apiFetch(`/api/public/admin/orders/${order.id}/gifts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ giftKey: selectedGiftKey }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || '附加失敗');
      alert('禮品已附加到此訂單');
      setShowGiftModal(false);
      setSelectedGiftKey('');
      await fetchOrder();
    } catch (err: any) {
      alert(err.message || '附加失敗');
    } finally {
      setAttachingGift(false);
    }
  };

  const fetchCustomerTags = async (phone: string) => {
    try {
      const res = await apiFetch(`/api/public/admin/customers/${phone}`);
      if (res.ok) {
        const data = await res.json();
        setCustomerTags(data.manualTags || []);
        setCustomerOrderCount((data.orders || []).length);
        setCustomerTotalSpent(
          (data.orders || []).reduce((sum: number, o: any) => sum + (o.total_price || 0), 0)
        );
      }
    } catch (e) { console.error('Failed to fetch customer tags:', e); }
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    const res = await apiFetch(`/api/public/admin/orders/${id}`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(order)
    });
    if (res.ok) { alert('更新成功'); navigate('/admin/orders'); }
    else { alert('更新失敗'); }
  };

  const handleConfirmPayment = async () => {
    if (!confirm('確認已收到此訂單的付款？')) return;
    setConfirming(true);
    const updatedOrder = { ...order, paymentConfirmed: 1 };
    const res = await apiFetch(`/api/public/admin/orders/${id}`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(updatedOrder)
    });
    if (res.ok) {
      setOrder({ ...order, paymentConfirmed: 1 });
      alert('付款已確認！客戶將收到 WhatsApp 通知。');
    } else { alert('確認失敗'); }
    setConfirming(false);
  };

  const handleCompleteOrder = async () => {
    if (!order) return;
    if (!confirm('確認標記此訂單為已完成？')) return;
    setCompleting(true);
    const res = await apiFetch(`/api/public/admin/orders/${order.id}/complete`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({})
    });
    if (res.ok) {
      alert('訂單已標記為完成');
      await fetchOrder();
    } else {
      alert('標記失敗');
    }
    setCompleting(false);
  };

  const handleKpayRefund = async () => {
    if (!order) return;
    const defaultAmount = order.finalPrice || 0;
    const amountInput = prompt(`輸入退款金額（HK$ cents，預設 ${defaultAmount}）：`, String(defaultAmount));
    if (amountInput === null) return;
    const refundAmount = Number(amountInput);
    if (!refundAmount || refundAmount <= 0) { alert('退款金額無效'); return; }

    const oriOrderNo = prompt('輸入 KPay 原交易訂單號（oriOrderNo，如已儲存可留空）：', String(order.kpayOrderNo || order.kpay_order_no || ''));
    if (oriOrderNo === null) return;

    if (!confirm(`確認向 KPay 申請退款 HK$${(refundAmount / 100).toFixed(2)}？`)) return;

    setRefunding(true);
    try {
      const res = await apiFetch(`/api/admin/orders/${order.id}/kpay/refund`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refundAmount, oriOrderNo: oriOrderNo || undefined })
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        const d = (data as any).data ?? data; // successResponse 信封：{success, data:{orderNo...}}
        alert(`退款申請已提交${d.orderNo ? `，退款單號：${d.orderNo}` : ''}`);
        await fetchOrder();
      } else {
        alert((data as any).error || (data as any).detail || '退款失敗');
      }
    } catch (e) {
      alert('網絡錯誤，請重試');
    } finally {
      setRefunding(false);
    }
  };

  const [resendingConfirmation, setResendingConfirmation] = useState(false);
  const [resendingCompletion, setResendingCompletion] = useState(false);

  const handleResendConfirmation = async () => {
    if (!order?.id) return;
    if (!confirm('重新發送訂單確認 WhatsApp 訊息給客戶？')) return;
    setResendingConfirmation(true);
    try {
      const res = await apiFetch(`/api/public/admin/orders/${order.id}/resend-confirmation`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      if (res.ok) {
        alert('確認訊息已重新發送');
      } else {
        const data = await res.json().catch(() => ({}));
        alert(data.error || '發送失敗');
      }
    } catch (e) {
      alert('發送失敗');
    } finally {
      setResendingConfirmation(false);
    }
  };

  const handleResendCompletion = async () => {
    if (!order?.id) return;
    if (!confirm('重新發送訂單完成 WhatsApp 訊息給客戶？')) return;
    setResendingCompletion(true);
    try {
      const res = await apiFetch(`/api/public/admin/orders/${order.id}/resend-completion`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      if (res.ok) {
        alert('完成訊息已重新發送');
      } else {
        const data = await res.json().catch(() => ({}));
        alert(data.error || '發送失敗');
      }
    } catch (e) {
      alert('發送失敗');
    } finally {
      setResendingCompletion(false);
    }
  };

  const handleLogout = () => {
    if (confirm('確定要登出嗎？')) { localStorage.removeItem('admin_token'); navigate('/admin'); }
  };

  if (loading) return (
    <AdminLayout currentPage="orders" onLogout={handleLogout}>
      <div className="flex items-center justify-center h-64 text-gray-500 dark:text-gray-400">加載中...</div>
    </AdminLayout>
  );
  if (!order) return (
    <AdminLayout currentPage="orders" onLogout={handleLogout}>
      <div className="text-center text-gray-500 dark:text-gray-400 py-12">訂單不存在</div>
    </AdminLayout>
  );

  // The admin API already parses items via parseOrderRow (array), while older
  // payloads / fallbacks may still be a JSON string — handle both.
  const orderItems: any[] = (() => {
    try {
      const raw: any = order.items;
      if (Array.isArray(raw)) return raw;
      return JSON.parse(raw || '[]');
    } catch { return []; }
  })();

  const ORDER_STATUS_LABELS: Record<string, string> = {
    pending_whatsapp: '待客戶 WhatsApp 確認',
    pending_payment: '待付款',
    pending_confirmation: '待確認收款',
    payment_confirmed: '已收款／處理中',
    processing: '備貨中',
    shipped: '已發貨',
    completed: '已完成',
    cancelled: '已取消',
  };

  // 一按複製：純文字訂單摘要（WhatsApp 轉發第三者用，例：供貨商／物流）
  const buildOrderSummaryText = (): string => {
    const lines: string[] = [];
    lines.push(`【Kare Tech 訂單 ${order.orderNum || ''}】`);
    lines.push(`客戶：${order.name || '-'}`);
    lines.push(`電話：${order.phone || '-'}`);
    lines.push(`落單時間：${new Date(order.createdAt * 1000).toLocaleString('zh-HK')}`);
    lines.push(`狀態：${ORDER_STATUS_LABELS[order.status] || order.status || '-'}`);
    if (order.paymentMethod) lines.push(`付款方式：${order.paymentMethod === 'fps' ? 'FPS 轉數快' : order.paymentMethod === 'payme' ? 'PayMe' : order.paymentMethod}`);
    lines.push('');
    lines.push('貨品：');
    for (const it of orderItems) {
      const qty = Number(it.quantity || 1);
      const subtotal = Number(it.subtotal ?? (Number(it.unitPrice || 0) * qty));
      lines.push(`• ${brandifyText(String(it.name || ''))} ×${qty} — ${formatPrice(subtotal)}`);
    }
    lines.push('');
    lines.push(`貨品總額：${formatPrice(order.totalPrice || 0)}`);
    const discount = Number(order.discountAmount || 0);
    if (discount > 0) lines.push(`折扣：-${formatPrice(discount)}`);
    lines.push(`應付總額：${formatPrice(order.finalPrice ?? order.totalPrice ?? 0)}`);
    lines.push('');
    lines.push(`送貨地區：${DELIVERY_REGION_LABELS[order.region] || order.region || '-'}`);
    lines.push(`地址：${order.address || '-'}`);
    if (order.deliveryRemark) lines.push(`送貨備註：${order.deliveryRemark}`);
    if (order.remarks) lines.push(`客戶備註：${order.remarks}`);
    return lines.join('\n');
  };

  const copyOrderSummary = async () => {
    const text = buildOrderSummaryText();
    let ok = false;
    try {
      await navigator.clipboard.writeText(text);
      ok = true;
    } catch {
      // 舊瀏覽器／非安全環境 fallback
      try {
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        ok = document.execCommand('copy');
        document.body.removeChild(ta);
      } catch { ok = false; }
    }
    // 非阻塞反饋（alert 會凍結頁面，自動化測試也會卡死）
    setCopied(ok ? 'ok' : 'fail');
    setTimeout(() => setCopied(null), 2000);
  };

  return (
    <AdminLayout currentPage="orders" onLogout={handleLogout}>
      {/* Header */}
      <div className="mb-6">
        <button onClick={() => navigate('/admin/orders')} className="text-red-600 hover:text-red-800 text-sm flex items-center gap-1 mb-2">
          <ArrowLeft className="w-4 h-4" /> 返回訂單列表
        </button>
        <div className="flex items-center gap-3 flex-wrap">
          <h1 className="text-2xl font-bold text-gray-800 dark:text-white">訂單 #{(order.orderNum || "-")}</h1>
          {order.orderCompleted === 1 ? (
            <span className="px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">訂單完成</span>
          ) : order.paymentConfirmed === 1 ? (
            <span className="px-3 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">已付款 / 處理中</span>
          ) : (
            <span className="px-3 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">待付款</span>
          )}
          <button
            onClick={copyOrderSummary}
            title="複製訂單摘要到剪貼簿，方便貼上 WhatsApp 轉發"
            className={`ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-bold border transition ${
              copied === 'ok'
                ? 'bg-green-600 text-white border-green-600'
                : copied === 'fail'
                ? 'bg-red-600 text-white border-red-600'
                : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:border-red-600 hover:text-red-600'
            }`}
          >
            <ClipboardCopy className="w-4 h-4" />
            {copied === 'ok' ? '已複製 ✓' : copied === 'fail' ? '複製失敗，請重試' : '複製訂單資料'}
          </button>
        </div>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">下單時間：{new Date(order.createdAt * 1000).toLocaleString('zh-HK')}</p>
      </div>

      {/* Tabs：訂單詳情／客戶對話 */}
      <div className="mb-6 flex gap-1 border-b border-gray-200 dark:border-gray-700">
        <button
          onClick={() => setActiveTab('details')}
          className={`flex items-center gap-2 px-5 py-2.5 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'details'
              ? 'border-red-600 text-red-600'
              : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
          }`}
        >
          <FileText className="w-4 h-4" />
          訂單詳情
        </button>
        <button
          onClick={() => setActiveTab('chat')}
          className={`flex items-center gap-2 px-5 py-2.5 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'chat'
              ? 'border-red-600 text-red-600'
              : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
          }`}
        >
          <MessageCircle className="w-4 h-4" />
          客戶對話
        </button>
      </div>

      {activeTab === 'chat' ? (
        /* 客戶對話 TAB：全寬對話面板（文字＋圖片附件） */
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
          <WhatsAppChatPanel
            phone={order.phone}
            heightClass="h-[calc(100vh-280px)]"
            inputPlaceholder="輸入消息通知客戶..."
          />
        </div>
      ) : (
      <div className="space-y-6">
        {/* Payment Proof */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <h2 className="text-lg font-bold text-gray-800 dark:text-white mb-4 flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-green-600" />
              付款記錄
            </h2>
            {order.paymentProof && (!order.uploads || order.uploads.length === 0) && (
              <div className="mb-4">
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">付款憑證：</p>
                <a href={order.paymentProof} target="_blank" rel="noopener noreferrer" className="text-red-600 hover:underline break-all text-sm">
                  {order.paymentProof}
                </a>
              </div>
            )}
            {order.uploads && order.uploads.length > 0 ? (
              <div className="grid grid-cols-4 gap-3 mb-4">
                {order.uploads.map((u: any) => (
                  <div key={u.id} className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                    {u.fileUrl?.match(/\.(jpg|jpeg|png|gif|webp)$/i) ? (
                      <a href={u.fileUrl} target="_blank" rel="noopener noreferrer">
                        <img src={u.fileUrl} alt="Proof" className="w-full h-24 object-cover hover:opacity-90" />
                      </a>
                    ) : (
                      <a href={u.fileUrl} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center h-24 text-red-600 text-xs bg-gray-50 dark:bg-gray-900">
                        查看文件
                      </a>
                    )}
                  </div>
                ))}
              </div>
            ) : !order.paymentProof && <p className="text-gray-400 dark:text-gray-500 text-sm italic mb-4">暫無付款憑證</p>}

            <div className="flex flex-col gap-3 pt-4 border-t border-gray-100 dark:border-gray-700">
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-3 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    className="w-5 h-5 text-green-600 rounded focus:ring-green-500"
                    checked={order.paymentConfirmed === 1}
                    disabled={confirming}
                    onChange={async (e) => {
                      const newValue = e.target.checked ? 1 : 0;
                      if (newValue === 1 && !confirm('確認已收到付款？系統將發送 WhatsApp 通知給客戶。')) return;
                      setConfirming(true);
                      const updatedOrder = { ...order, paymentConfirmed: newValue };
                      const res = await apiFetch(`/api/public/admin/orders/${id}`, {
                        method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(updatedOrder)
                      });
                      if (res.ok) { setOrder({...order, paymentConfirmed: newValue}); }
                      else { alert('更新失敗'); e.target.checked = !e.target.checked; }
                      setConfirming(false);
                    }}
                  />
                  <span className={`font-bold ${order.paymentConfirmed ? 'text-green-600' : 'text-gray-600 dark:text-gray-400'}`}>
                    {order.paymentConfirmed ? '已確認付款' : '未確認付款'}
                  </span>
                </label>
                {!order.paymentConfirmed && (
                  <button
                    onClick={handleConfirmPayment}
                    disabled={confirming}
                    className="bg-green-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-green-700 disabled:opacity-50 transition-colors"
                  >
                    {confirming ? '處理中...' : '一鍵確認付款'}
                  </button>
                )}
              </div>

              <div className="flex items-center gap-2 pt-2">
                <button
                  onClick={handleResendConfirmation}
                  disabled={resendingConfirmation}
                  className="text-xs bg-blue-50 text-blue-600 border border-blue-200 hover:bg-blue-100 px-3 py-1.5 rounded-lg transition-colors disabled:opacity-50"
                >
                  {resendingConfirmation ? '發送中...' : '重發確認 WhatsApp'}
                </button>
                {order.paymentConfirmed === 1 && (
                  <button
                    onClick={handleKpayRefund}
                    disabled={refunding}
                    className="text-xs bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100 px-3 py-1.5 rounded-lg transition-colors disabled:opacity-50"
                  >
                    {refunding ? '退款中...' : 'KPay 退款'}
                  </button>
                )}
              </div>

            </div>
          </div>

          {/* 順豐物流（sf-express 模組） */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <h2 className="text-lg font-bold text-gray-800 dark:text-white mb-4 flex items-center gap-2 flex-wrap">
              🚚 順豐物流
              {sfData?.shipment?.waybillNo && (
                <span className="text-sm font-mono font-normal text-gray-500 dark:text-gray-400">{sfData.shipment.waybillNo}</span>
              )}
              {sfData?.shipment?.status && (
                <span className="text-xs px-2 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
                  {SF_STATUS_LABELS[sfData.shipment.status] || sfData.shipment.status}
                </span>
              )}
            </h2>
            {sfMsg && (
              <div className="mb-3 text-sm bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 px-3 py-2 rounded-lg break-all">{sfMsg}</div>
            )}
            {!sfData?.shipment ? (
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">此訂單尚未建立順豐運單（順豐到付模式）。</p>
                <button
                  onClick={() => sfAction('create')}
                  disabled={sfBusy}
                  className="bg-red-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-red-700 disabled:opacity-50"
                >
                  {sfBusy ? '處理中...' : '建立順豐運單'}
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="flex flex-wrap gap-2">
                  <button onClick={sfRoutes} disabled={sfBusy}
                    className="text-xs bg-indigo-50 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-800 hover:bg-indigo-100 px-3 py-1.5 rounded-lg disabled:opacity-50">
                    {sfBusy ? '處理中...' : '查詢軌跡'}
                  </button>
                  <button onClick={() => sfAction('waybill')} disabled={sfBusy}
                    className="text-xs bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-300 border border-blue-200 dark:border-blue-800 hover:bg-blue-100 px-3 py-1.5 rounded-lg disabled:opacity-50">
                    列印面單
                  </button>
                  <button onClick={() => { if (confirm('確定取消此順豐運單？')) sfAction('cancel'); }} disabled={sfBusy}
                    className="text-xs bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 border border-red-200 dark:border-red-800 hover:bg-red-100 px-3 py-1.5 rounded-lg disabled:opacity-50">
                    取消運單
                  </button>
                </div>
                {(sfData?.routes?.localEvents || []).length > 0 && (
                  <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-3 max-h-48 overflow-y-auto">
                    <p className="text-xs font-bold text-gray-500 dark:text-gray-400 mb-2">軌跡（本站已收推送）</p>
                    <ol className="space-y-1">
                      {(sfData.routes.localEvents as any[]).slice().reverse().map((ev) => (
                        <li key={ev.id} className="text-xs text-gray-600 dark:text-gray-300">
                          <span className="text-gray-400">{ev.accepted_time || ''}</span>{' '}
                          <b>{ev.op_desc || ev.op_code || '事件'}</b>{ev.location ? ` · ${ev.location}` : ''}
                        </li>
                      ))}
                    </ol>
                  </div>
                )}
                {sfPrint && (
                  <pre className="text-[10px] bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg p-3 max-h-48 overflow-auto whitespace-pre-wrap">{sfPrint}</pre>
                )}
              </div>
            )}
            <p className="text-[11px] text-gray-400 mt-3">
              憑證未配置（SF_SANDBOX_* / SF_PROD_*）時操作會返回錯誤提示——屬預期。申請清單見 docs/sf-bridge-onboarding-checklist.md
            </p>
          </div>

          {/* Order Info (Read Only) */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-gray-800 dark:text-white">訂單資料（只讀）</h2>
              <button
                onClick={handleResendCompletion}
                disabled={resendingCompletion}
                className="text-xs bg-green-50 text-green-600 border border-green-200 hover:bg-green-100 px-3 py-1.5 rounded-lg transition-colors disabled:opacity-50"
              >
                {resendingCompletion ? '發送中...' : '重發完成 WhatsApp'}
              </button>
            </div>
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1 flex items-center gap-1"><User className="w-3 h-3" /> 姓名</label>
                <div className="w-full p-2.5 bg-gray-100 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-700 dark:text-gray-300">{order.name}</div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1 flex items-center gap-1"><Phone className="w-3 h-3" /> 電話</label>
                <div className="w-full p-2.5 bg-gray-100 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-700 dark:text-gray-300">{order.phone}</div>
              </div>
              <div className="col-span-2">
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1 flex items-center gap-1"><MapPin className="w-3 h-3" /> 地址</label>
                <div className="w-full p-2.5 bg-gray-100 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-700 dark:text-gray-300">{DELIVERY_REGION_LABELS[order.region] || order.region} {order.address}</div>
              </div>

            </div>

            {/* Customer Tags */}
            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 flex items-center gap-1">
                  <Tag className="w-3 h-3" /> 客戶標籤
                </label>
                <button
                  type="button"
                  onClick={() => setShowTagModal(true)}
                  className="text-xs text-purple-600 hover:text-purple-800 font-medium px-2 py-1 border border-purple-200 rounded hover:bg-purple-50 transition"
                >
                  管理標籤
                </button>
              </div>
              <div className="bg-gray-50 dark:bg-gray-900 p-3 rounded-lg border border-gray-200 dark:border-gray-700">
                <div className="flex flex-wrap gap-2">
                  {(() => {
                    const tags: { label: string; color: string }[] = [];
                    if (customerOrderCount === 1) tags.push({ label: '新客戶', color: 'bg-blue-100 text-blue-700 border-blue-200' });
                    else if (customerOrderCount >= 2 && customerOrderCount < 5) tags.push({ label: '回頭客', color: 'bg-purple-100 text-purple-700 border-purple-200' });
                    if (customerOrderCount >= 5) tags.push({ label: 'VIP', color: 'bg-yellow-100 text-yellow-700 border-yellow-200' });
                    if ((order.finalPrice || order.totalPrice) >= 50000) tags.push({ label: '大單', color: 'bg-amber-100 text-amber-700 border-amber-200' });
                    const computedLabels = tags.map(t => t.label);
                    const displayTags = [
                      ...tags,
                      ...customerTags
                        .filter((t: any) => !computedLabels.includes(t.tag))
                        .map((t: any) => ({ label: t.tag, color: `bg-${t.color}-100 text-${t.color}-700 border-${t.color}-200` })),
                    ];
                    if (displayTags.length === 0) {
                      return <span className="text-xs text-gray-400 italic">暫無標籤</span>;
                    }
                    return displayTags.map((t, i) => (
                      <span key={i} className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium border ${t.color}`}>
                        {t.label}
                      </span>
                    ));
                  })()}
                </div>
              </div>
            </div>

            {/* Items — horizontal rows: thumbnail left, name/qty middle, subtotal right. Mobile-friendly full-width rows. */}
            <div className="mb-4">
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">產品明細</label>
              <div className="bg-gray-50 dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                {orderItems.map((item: any, idx: number) => {
                  // brandifyText: display-only — historical snapshots still store
                  // the raw X-1493 name; render them consistently as «X-1493».
                  const itemName = brandifyText(String(item.name
                    ? item.name
                    : item.packageType
                      ? (item.packageType === '2-dish-1-soup' ? '2餸1湯' : '3餸1湯')
                      : (item.productId || '商品')));
                  const qty = item.quantity || 1;
                  const img = productImages[Number(item.productId)];
                  return (
                    <div key={idx} className="flex items-center gap-3 px-3 py-2.5 border-b border-gray-200 dark:border-gray-700 last:border-b-0">
                      {img ? (
                        <img src={img} alt={String(itemName)} className="w-12 aspect-square rounded-lg object-cover flex-shrink-0 bg-white" />
                      ) : (
                        <div className="w-12 aspect-square rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 flex items-center justify-center text-lg flex-shrink-0">🌶️</div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-gray-800 dark:text-gray-100 break-words">{itemName}</div>
                        {item.unitPrice != null && (
                          <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{formatPrice(item.unitPrice)} × {qty}</div>
                        )}
                      </div>
                      <div className="text-sm font-semibold text-gray-800 dark:text-gray-100 whitespace-nowrap">{formatPrice(item.subtotal || (item.unitPrice * qty))}</div>
                    </div>
                  );
                })}
                <div className="border-t border-gray-200 dark:border-gray-700 p-3 space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span>小計</span>
                    <span>{formatPrice(order.totalPrice)}</span>
                  </div>
                  {order.discountAmount > 0 && (
                    <div className="flex justify-between text-green-600">
                      <span>優惠{order.giftCode ? ` (${order.giftCode})` : ''}</span>
                      <span>-{formatPrice(order.discountAmount)}</span>
                    </div>
                  )}
                  {(() => {
                    const deliveryFee = order.finalPrice - order.totalPrice + order.discountAmount;
                    if (deliveryFee <= 0) return null;
                    return (
                      <div className="flex justify-between">
                        <span>運費</span>
                        <span>{formatPrice(deliveryFee)}</span>
                      </div>
                    );
                  })()}
                  <div className="flex justify-between font-bold pt-1">
                    <span>總計</span>
                    <span>{formatPrice(order.finalPrice)}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Attached gifts */}
            <div className="mb-4">
              <div className="flex items-center justify-between mb-1">
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 flex items-center gap-1">
                  <Gift className="w-3 h-3" /> 隨單附送禮品
                </label>
                <button
                  type="button"
                  onClick={() => setShowGiftModal(true)}
                  className="text-xs flex items-center gap-1 text-red-600 hover:text-red-700 font-medium"
                >
                  <Plus size={12} /> 附加禮品
                </button>
              </div>
              {(order.attachedGifts || []).length > 0 ? (
                <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 p-3 rounded-lg space-y-2">
                  {(order.attachedGifts as any[]).map((g: any) => (
                    <div key={g.id} className="flex justify-between text-sm">
                      <span className="text-amber-800 dark:text-amber-200">{g.giftName || g.description || '禮品'} x {g.quantity || 1}</span>
                      <span className="text-xs text-amber-600 dark:text-amber-300 uppercase">{g.status}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-xs text-gray-400 dark:text-gray-500">此訂單暫無隨單禮品</div>
              )}
            </div>

            {/* Customer Remarks (read-only) */}
            {order.remarks && (
              <div className="mb-4">
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">客戶備註</label>
                <div className="w-full p-2.5 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                  {order.remarks}
                </div>
              </div>
            )}

            {/* Admin Editable Section */}
            <form onSubmit={handleUpdate} className="border-t border-gray-100 dark:border-gray-700 pt-4">
              <h3 className="text-sm font-bold text-gray-700 dark:text-gray-300 mb-3">管理員編輯區</h3>
              <div className="mb-4">
                <label className="block text-xs font-medium text-gray-500 mb-1 text-red-500">管理員備註</label>
                <textarea className="w-full p-2.5 border border-red-200 bg-red-50 rounded-lg text-sm focus:ring-2 focus:ring-red-500 outline-none resize-none" rows={2} value={order.adminRemarks || ''} onChange={e => setOrder({...order, adminRemarks: e.target.value})} />
              </div>
              {order.paymentConfirmed === 1 && order.orderCompleted === 0 && isAdmin && (
                <div className="mb-4">
                <button
                  type="button"
                  onClick={handleCompleteOrder}
                  disabled={completing}
                  className="bg-green-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-green-700 disabled:opacity-50 transition-colors"
                >
                  {completing ? '處理中...' : '標記訂單完成'}
                </button>
              </div>
              )}
              <div className="flex gap-2">
                <button type="submit" className="bg-red-600 text-white px-6 py-2.5 rounded-lg text-sm font-medium hover:bg-red-700 transition-colors">儲存備註</button>
                <button type="button" onClick={() => navigate('/admin/orders')} className="bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 px-6 py-2.5 rounded-lg text-sm font-medium hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors">取消</button>
              </div>
            </form>
          </div>
      </div>
      )}

      {/* Attach Gift Modal */}
      {showGiftModal && order && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full">
            <form onSubmit={handleAttachGift} className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                  <Gift className="w-5 h-5 text-red-600" /> 附加隨單禮品
                </h2>
                <button type="button" onClick={() => setShowGiftModal(false)} className="text-gray-400 hover:text-gray-600"><X size={22} /></button>
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">選擇禮品 *</label>
                <select
                  value={selectedGiftKey}
                  onChange={(e) => setSelectedGiftKey(e.target.value)}
                  required
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg outline-none bg-white dark:bg-gray-800 dark:text-white"
                >
                  <option value="">請選擇</option>
                  {giftCatalog.map((item: any) => (
                    <option key={item.key} value={item.key}>{item.name}</option>
                  ))}
                </select>
                {giftCatalog.length === 0 && (
                  <p className="text-xs text-red-500 mt-1">沒有啟用的禮品，請先到「禮品目錄」建立。</p>
                )}
              </div>
              <div className="flex justify-end gap-3">
                <button type="button" onClick={() => setShowGiftModal(false)} className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm hover:bg-gray-50 dark:hover:bg-gray-700 dark:text-white">取消</button>
                <button
                  type="submit"
                  disabled={attachingGift || !selectedGiftKey}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm hover:bg-red-700 disabled:opacity-50 flex items-center gap-2"
                >
                  <Save size={16} /> {attachingGift ? '處理中...' : '儲存'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Tag Manager Modal */}
      {showTagModal && order && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-lg w-full max-h-[80vh] overflow-y-auto">
            <div className="p-6">
              <TagManager
                phone={order.phone}
                name={order.name}
                orderCount={customerOrderCount}
                totalSpent={customerTotalSpent}
                manualTags={customerTags}
                onTagsChange={() => {
                  fetchCustomerTags(order.phone);
                }}
              />
              <div className="mt-6 flex justify-end">
                <button
                  onClick={() => setShowTagModal(false)}
                  className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg text-sm hover:bg-gray-300 dark:hover:bg-gray-600 transition"
                >
                  關閉
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminOrderDetail;
